// Mui
import {
    Box,
    Table,
    Typography,
    TextField,
    InputAdornment,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Button,
    Skeleton,
    ClickAwayListener,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RestoreIcon from "@mui/icons-material/Restore";

// Icons
import icons from "enums/icons";

// Config
import { selectedUserstableHeaderConfig, tableHeaderConfig } from "./config";

// Components
import { TagName } from "components/TagName";
import { SortMenu } from "components/SortMenu";
import CustomIcon from "components/CustomIcon";
import { FormattedMessage, useIntl } from "react-intl";
import { TruncatedText } from "components/TruncatedText";
import NavButton from "components/HeaderNavigation/NavButton";
import HubAddUsersStyles from "pages/HubAddUsers/HubAddUsers.styles";
import { Header, Body } from "components/Layout/LayoutContent";
import { Checkbox } from "components/Checkbox";
import TablePagination from "components/Talent/TablePagination";
import { InviteUsersModal } from "pages/HubManagement/components/InviteUsers";
import PersonWorkHistory from "components/PersonWorkHistory";

// Hooks
import useHubAddUsers from "./useHubAddUsers";
import { UsersTableRow } from "./UsersTableRow";
import { isEmpty, isEqual } from "lodash";
import { Order } from "components/SortMenu/SortMenu";

const HubAddUsers = () => {
    const intl = useIntl();
    const classes = HubAddUsersStyles();

    const {
        users,
        selectedUsers,
        filters,
        usersSort,
        selectedUsersSort,
        inputPage,
        paginationTextInputRef,
        activeTab,
        inviteModalOpen,
        selectAllCheckboxes,
        pendingInvitations,
        org_id,
        isFetching,
        toggleShowWorkHistory,
        WHAnchorEl,
        personInfo,
        filtersInitialValue,
        hub,
        usersWidthPercentage,
        selectedUsersWidthPercentage,
        selectedUsersMaxWidth,
        tablesWrapperRef,
        handleFetchData,
        handleUsersSort,
        handleSelectUsersSort,
        handleFiltersChange,
        handleAddUser,
        handleRemoveUser,
        handleAddMultipleUsers,
        handleRemoveAllUsers,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        toggleCheckUser,
        toggleCheckAllUsers,
        toggleCheckSelectedUser,
        toggleCheckAllSelectedUsers,
        handleOpenInvitationModal,
        handleInviteSelectedUsers,
        closeInviteModal,
        handleExitPage,
        handleSaveUsers,
        handleClearFilters,
        onCloseWH,
        onClickRestoreIcon,
    } = useHubAddUsers();

    return (
        <>
            <Header backgroundColor="primary">
                <Box className={classes.headerInner}>
                    <Box>
                        <Typography className={classes.headerTitle}>
                            {intl.formatMessage({ id: "hubManagement.client.addUsers.title" })}
                            {!isFetching && (
                                <TagName
                                    sx={{
                                        fontSize: 24,
                                        fontWeight: 600,
                                        borderWidth: 2,
                                        borderRadius: 2,
                                        ml: 2,
                                        px: 1.5,
                                    }}
                                >
                                    {hub.name}
                                </TagName>
                            )}
                        </Typography>
                    </Box>
                    <Box>
                        <NavButton
                            isBackButton
                            variant="text"
                            onClick={handleExitPage}
                            translationId="hubManagement.client.addUsers.exitPage"
                        />
                        <NavButton
                            showBackArrow={false}
                            disabled={!selectedUsers.length}
                            onClick={handleSaveUsers}
                            startIcon={<CustomIcon icon={icons.check} />}
                            translationId="hubManagement.client.button.addUsers"
                        />
                    </Box>
                </Box>
            </Header>

            <Body>
                <Box className={classes.bodyHeader}>
                    <Typography variant="h6" className={classes.intro}>
                        {intl.formatMessage({ id: "hubManagement.client.addUsers.intro" })}
                    </Typography>
                    <Box className={classes.toolbox}>
                        <Box className={classes.filtersWrapper}>
                            <Box sx={{ maxWidth: 290, mr: 2 }}>
                                <TextField
                                    size="small"
                                    id="user-filter"
                                    name="user-filter"
                                    autoComplete="off"
                                    value={filters.filter_user_name}
                                    className={classes.filterByNameInput}
                                    onChange={handleFiltersChange}
                                    placeholder={intl.formatMessage({
                                        id: "hubManagement.client.addUsers.filters.byUser",
                                    })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                            <Box sx={{ maxWidth: 290, mr: 1 }}>
                                <TextField
                                    size="small"
                                    id="company-filter"
                                    name="company-filter"
                                    autoComplete="off"
                                    value={filters.filter_company_name}
                                    className={classes.filterByNameInput}
                                    onChange={handleFiltersChange}
                                    placeholder={intl.formatMessage({
                                        id: "hubManagement.client.addUsers.filters.byCompany",
                                    })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <CustomIcon icon={icons.searchInverted} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Box>
                        <NavButton
                            variant="outlined"
                            showBackArrow={false}
                            onClick={handleInviteSelectedUsers}
                            disabled={users.status === "fetching"}
                            startIcon={<CustomIcon icon={icons.plus} />}
                            translationId="hubManagement.client.addUsers.inviteUser"
                        />
                    </Box>

                    <Box display="flex" flexDirection="column" width="fit-content" justifySelf="flex-end">
                        {pendingInvitations.length > 0 && (
                            <Button
                                onClick={handleOpenInvitationModal}
                                className={classes.invitationsButton}
                                sx={{ marginTop: "8px" }}
                                variant="text"
                            >
                                {intl.formatMessage({
                                    id: "hubManagement.client.managePendingInvitations",
                                })}{" "}
                                ({pendingInvitations.length})
                            </Button>
                        )}
                    </Box>
                </Box>
                <Box>
                    <Box className={classes.tablesWrapper} ref={tablesWrapperRef}>
                        {/* Users */}
                        <Box className={classes.usersTable} width={`${usersWidthPercentage}%`}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <TablePagination
                                    currentPage={users.meta.page_no}
                                    totalPages={users.meta.pages}
                                    pageValue={inputPage}
                                    onPageChange={handlePageChange}
                                    handleInputChange={handleInputChange}
                                    handleOnBlur={handleOnBlur}
                                    handleOnKeyDown={handleOnKeyDown}
                                    inputRef={paginationTextInputRef}
                                />
                                <Button
                                    variant="text"
                                    className={classes.selectButton}
                                    disabled={!users.data.some((user) => user.checked)}
                                    onClick={handleAddMultipleUsers}
                                    startIcon={<CheckCircleRoundedIcon sx={{ color: "success.main" }} />}
                                >
                                    {intl.formatMessage({ id: "hubManagement.client.button.addSelectedUsers" })}
                                </Button>
                            </Box>
                            <Table>
                                <TableHead className={classes.tableHeader}>
                                    <TableRow>
                                        <TableCell>
                                            <Checkbox
                                                size="small"
                                                disabled={!users.data.length || users.status === "fetching"}
                                                onChange={toggleCheckAllUsers}
                                                checked={selectAllCheckboxes.users}
                                                inputProps={{
                                                    "aria-label": "Select all users",
                                                }}
                                            />
                                        </TableCell>
                                        {tableHeaderConfig
                                            .filter((cell) => !cell.isHidden)
                                            .map((cell) => {
                                                return (
                                                    <TableCell key={cell.id} sx={cell.sx}>
                                                        {!isEmpty(cell.sort) ? (
                                                            <Box className={classes.cellWithSortButton}>
                                                                <SortMenu
                                                                    columnId={cell.id}
                                                                    currentSortBy={usersSort}
                                                                    sortConfig={cell.sort}
                                                                    icon={
                                                                        <CustomIcon
                                                                            className={classes.sortButton}
                                                                            icon={icons.sortArrows}
                                                                        />
                                                                    }
                                                                    {...(!isEmpty(cell.sort)
                                                                        ? {
                                                                              onSort: (order, columnId) =>
                                                                                  handleUsersSort(order, columnId),
                                                                          }
                                                                        : {})}
                                                                />
                                                                <Typography>
                                                                    {cell.name}{" "}
                                                                    {cell.id === "name" ? `(${users.meta.hits})` : ""}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography>{cell.name}</Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        <TableCell>&nbsp;</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={classes.tableBody}>
                                    {users.status === "done" &&
                                        users.data.map((col) => {
                                            const isUserSelected = Boolean(
                                                selectedUsers.find((user) => user.id === col.id)
                                            );
                                            return (
                                                <UsersTableRow
                                                    key={col.id}
                                                    data={col}
                                                    width={200}
                                                    selectedUsers={selectedUsers}
                                                    Checkbox={
                                                        <Checkbox
                                                            size="small"
                                                            checked={col.checked}
                                                            disabled={isUserSelected}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(ev) => toggleCheckUser(ev, col.id)}
                                                            inputProps={{
                                                                "aria-label": "Select user",
                                                            }}
                                                        />
                                                    }
                                                    AddUserButton={
                                                        <IconButton
                                                            className={classes.addUserButton}
                                                            size="small"
                                                            onClick={(event) =>
                                                                handleAddUser(event, col, isUserSelected)
                                                            }
                                                        >
                                                            {isUserSelected ? (
                                                                <CancelIcon sx={{ color: "error.main" }} />
                                                            ) : (
                                                                <CheckCircleRoundedIcon />
                                                            )}
                                                        </IconButton>
                                                    }
                                                    WHButton={
                                                        <IconButton
                                                            size="small"
                                                            sx={{ color: "primary.dark", p: 0, mr: 1 }}
                                                            onClick={(event) => onClickRestoreIcon(event, col.id)}
                                                            disabled={!col.person_id}
                                                        >
                                                            <RestoreIcon sx={{ fontSize: 24 }} />
                                                        </IconButton>
                                                    }
                                                />
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </Box>

                        {/* Selected Users */}
                        <Box className={classes.selectedUsersTableWrapper} width={`${selectedUsersWidthPercentage}%`}>
                            <Box height={48} display="flex" alignItems="center" justifyContent="flex-end">
                                <Button
                                    variant="text"
                                    className={classes.selectButton}
                                    onClick={handleRemoveAllUsers}
                                    disabled={!selectedUsers.some((user) => user.checked)}
                                    startIcon={<CancelIcon sx={{ color: "error.main" }} />}
                                >
                                    {intl.formatMessage({ id: "hubManagement.client.addUsers.button.removeUsers" })}
                                </Button>
                            </Box>
                            <Box className={classes.selectedUsersTable}>
                                <Table>
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell>
                                                <Checkbox
                                                    size="small"
                                                    disabled={!selectedUsers.length || users.status === "fetching"}
                                                    checked={selectAllCheckboxes.selectedUsers}
                                                    onChange={toggleCheckAllSelectedUsers}
                                                    inputProps={{
                                                        "aria-label": "Select all selected users",
                                                    }}
                                                />
                                            </TableCell>
                                            {selectedUserstableHeaderConfig.map((cell) => {
                                                return (
                                                    <TableCell key={cell.id} sx={cell.sx}>
                                                        {cell.isSortable ? (
                                                            <Box className={classes.cellWithSortButton}>
                                                                <SortMenu
                                                                    columnId={cell.id}
                                                                    currentSortBy={selectedUsersSort}
                                                                    sortConfig={cell.sort}
                                                                    icon={
                                                                        <CustomIcon
                                                                            className={classes.sortButton}
                                                                            icon={icons.sortArrows}
                                                                        />
                                                                    }
                                                                    {...(!isEmpty(cell.sort)
                                                                        ? {
                                                                              onSort: (order, columnId) =>
                                                                                  handleSelectUsersSort(
                                                                                      order as unknown as Order,
                                                                                      columnId
                                                                                  ),
                                                                          }
                                                                        : {})}
                                                                />
                                                                <Typography>
                                                                    {cell.name} ({selectedUsers.length})
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Typography>{cell.name}</Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell>&nbsp;</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody className={classes.tableBody}>
                                        {users.status === "done" &&
                                            selectedUsers.map((col) => (
                                                <TableRow key={col.id}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            size="small"
                                                            checked={col.checked}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(ev) => toggleCheckSelectedUser(ev, col.id)}
                                                            inputProps={{
                                                                "aria-label": "Select selected user",
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                maxWidth: `${selectedUsersMaxWidth}px`,
                                                                display: "flex",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: "secondary.dark", pl: 0 }}
                                                                onClick={(event) => onClickRestoreIcon(event, col.id)}
                                                                disabled={!col.person_id}
                                                                aria-label="work history button"
                                                            >
                                                                <RestoreIcon sx={{ fontSize: 24 }} />
                                                            </IconButton>
                                                            <TruncatedText width={selectedUsersMaxWidth}>
                                                                {col.name}
                                                            </TruncatedText>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            className={classes.addUserButton}
                                                            size="small"
                                                            onClick={(event) => handleRemoveUser(event, col)}
                                                        >
                                                            <CancelIcon sx={{ color: "error.main" }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    </Box>

                    {/* Fetching */}
                    {users.status === "fetching" && (
                        <Table>
                            <TableBody className={classes.tableBody}>
                                {Array.from(Array(6)).map((el, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}>
                                            <Skeleton variant="rounded" animation="wave" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {/* Other States */}
                    {users.status === "done" && !Boolean(users.data.length) && (
                        <Table>
                            <TableBody className={classes.tableBody}>
                                {/* No match */}
                                {!isEqual(filters, filtersInitialValue) && (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Box className={classes.noDataWrapper}>
                                                <Typography>
                                                    <FormattedMessage
                                                        id="hubManagement.client.usersTable.noMatchesMessage1"
                                                        values={{ string: filters.filter_user_name }}
                                                    />
                                                </Typography>
                                                <Typography>
                                                    <FormattedMessage id="hubManagement.client.usersTable.noMatchesMessage2" />
                                                </Typography>
                                                <Box display="flex">
                                                    <NavButton
                                                        showBackArrow={false}
                                                        variant="outlined"
                                                        onClick={handleClearFilters}
                                                        startIcon={
                                                            <CustomIcon icon={icons.close} sx={{ fontSize: 14 }} />
                                                        }
                                                        translationId="hubManagement.client.button.clearSearch"
                                                        sx={{ marginRight: 2 }}
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* No data */}
                                {isEqual(filters, filtersInitialValue) && (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Box className={classes.noDataWrapper}>
                                                <Typography>
                                                    <FormattedMessage id="hubManagement.client.usersTable.noDataMessage" />
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                <InviteUsersModal
                    open={inviteModalOpen}
                    activeTab={activeTab}
                    onClose={closeInviteModal}
                    pendingInvitations={pendingInvitations}
                    org_id={Number(org_id)}
                    fetchUsersData={() =>
                        handleFetchData({
                            ...filters,
                            order_by: `${usersSort.columnId},${usersSort.direction}`,
                            page_no: Number(inputPage),
                        })
                    }
                />

                {toggleShowWorkHistory && (
                    <ClickAwayListener onClickAway={onCloseWH}>
                        <PersonWorkHistory
                            role={""}
                            personId={personInfo.id}
                            anchorEl={WHAnchorEl}
                            onClose={onCloseWH}
                            open={toggleShowWorkHistory}
                        />
                    </ClickAwayListener>
                )}
            </Body>
        </>
    );
};

export default HubAddUsers;
